import React, { useEffect, useMemo, useState } from "react";
import { Plus, FileText, Trash2, Pencil, Archive, RotateCcw, Printer, X, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import MoneyInput from "@/components/MoneyInput";
import UnitAttachments from "@/components/UnitAttachments";

const STATUS_LABEL = { taslak: "Taslak", aktif: "Aktif", tamamlandi: "Tamamlandı", iptal: "İptal" };
const STATUS_TONE = {
  taslak: "bg-secondary text-muted-foreground",
  aktif: "bg-brand/15 text-brand",
  tamamlandi: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  iptal: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]",
};
const UT_LABEL = { m2: "m²", adet: "adet", goturu: "götürü" };

const EMPTY_FORM = {
  cari_id: "", project_id: "", work_type: "", unit_type: "m2",
  unit_price: 0, quantity: 0, total_amount: 0,
  start_date: todayISO(), end_date: "",
  payment_plan: [], notes: "", status: "taslak",
};

export default function Sozlesmeler() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [archived, setArchived] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachmentSub, setAttachmentSub] = useState(null);

  const load = async () => {
    const [{ data: a }, { data: b }, { data: cr }, { data: pr }] = await Promise.all([
      api.get("/subcontracts?archived=false"),
      api.get("/subcontracts?archived=true"),
      api.get("/cariler?archived=false"),
      api.get("/projects?archived=false"),
    ]);
    setItems(a); setArchived(b);
    // Only taseron/tedarikci/personel eligible for subcontracts
    setCariler(cr.filter((c) => ["taseron", "tedarikci", "personel"].includes(c.type)));
    setProjects(pr);
  };

  useEffect(() => { load(); }, []);

  const shown = tab === "archive" ? archived : items;

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, start_date: todayISO() });
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({
      cari_id: s.cari_id,
      project_id: s.project_id,
      work_type: s.work_type || "",
      unit_type: s.unit_type || "m2",
      unit_price: s.unit_price || 0,
      quantity: s.quantity || 0,
      total_amount: s.total_amount || 0,
      start_date: s.start_date || todayISO(),
      end_date: s.end_date || "",
      payment_plan: s.payment_plan || [],
      notes: s.notes || "",
      status: s.status || "taslak",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        unit_price: Number(form.unit_price),
        quantity: Number(form.quantity),
        total_amount: Number(form.total_amount),
        payment_plan: (form.payment_plan || []).map((p) => ({
          date: p.date, description: p.description || "",
          amount: Number(p.amount), paid: !!p.paid,
        })),
      };
      if (editingId) {
        await api.patch(`/subcontracts/${editingId}`, payload);
        toast.success("Sözleşme güncellendi");
      } else {
        await api.post("/subcontracts", payload);
        toast.success("Sözleşme oluşturuldu");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const archive = async (id) => {
    if (!window.confirm("Bu sözleşmeyi arşive taşımak istediğinize emin misiniz?")) return;
    await api.delete(`/subcontracts/${id}`);
    toast.success("Arşive taşındı");
    load();
  };
  const restore = async (id) => {
    await api.post(`/subcontracts/${id}/restore`);
    toast.success("Geri getirildi");
    load();
  };
  const purge = async (id) => {
    if (!window.confirm("Bu sözleşmeyi KALICI olarak silmek istediğinize emin misiniz?")) return;
    await api.delete(`/subcontracts/${id}/purge`);
    toast.success("Kalıcı olarak silindi");
    load();
  };

  const downloadPdf = (id) => {
    // Open PDF directly via same-origin URL — cookie auth is automatic.
    // Sync window.open (from user click) bypasses popup blockers.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/subcontracts/${id}/pdf`;
    window.open(url, "_blank", "noopener");
  };

  const addPlanRow = () => {
    setForm({ ...form, payment_plan: [...(form.payment_plan || []), { date: todayISO(), amount: 0, description: "", paid: false }] });
  };
  const removePlanRow = (i) => {
    const p = [...(form.payment_plan || [])];
    p.splice(i, 1);
    setForm({ ...form, payment_plan: p });
  };
  const updatePlanRow = (i, key, val) => {
    const p = [...(form.payment_plan || [])];
    p[i] = { ...p[i], [key]: val };
    setForm({ ...form, payment_plan: p });
  };

  // Auto-compute total_amount when unit_price × quantity are changed (for m2/adet)
  const recalcTotal = (up, qty) => {
    if (form.unit_type !== "goturu") {
      setForm((f) => ({ ...f, unit_price: up, quantity: qty, total_amount: Number(up) * Number(qty) }));
    } else {
      setForm((f) => ({ ...f, unit_price: up, quantity: qty }));
    }
  };

  const planTotal = useMemo(
    () => (form.payment_plan || []).reduce((s, p) => s + Number(p.amount || 0), 0),
    [form.payment_plan]
  );

  return (
    <div className="space-y-6" data-testid="sozlesmeler-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Taşeron & Tedarikçi</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Sözleşmeler</h1>
          <p className="text-sm text-muted-foreground mt-1">Taşeron ve tedarikçi anlaşmaları, iş sözleşmeleri, ödeme takvimi.</p>
        </div>
        {hasPerm("sozlesmeler.create") && (
          <Button data-testid="new-sub-btn" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2" /> Yeni Sözleşme
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="sub-tabs">
          <TabsTrigger value="active" data-testid="tab-active">Aktif ({items.length})</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive"><Archive className="w-3 h-3 mr-1" /> Arşiv ({archived.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Sözleşme No</TableHead>
                <TableHead>Taşeron/Tedarikçi</TableHead>
                <TableHead>Proje</TableHead>
                <TableHead>İş Tanımı</TableHead>
                <TableHead className="text-right">Toplam</TableHead>
                <TableHead className="text-right">Hakediş</TableHead>
                <TableHead className="text-right">Kalan</TableHead>
                <TableHead>Durum</TableHead>
                <TableHead className="w-32"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((s) => (
                <TableRow key={s.id} data-testid={`sub-row-${s.id}`} className={`hover:bg-secondary/40 ${tab === "archive" ? "opacity-70" : ""}`}>
                  <TableCell className="text-xs font-mono">{s.contract_no || "-"}</TableCell>
                  <TableCell className="text-sm">{cariler.find((c) => c.id === s.cari_id)?.name || "-"}</TableCell>
                  <TableCell className="text-sm">{projects.find((p) => p.id === s.project_id)?.name || "-"}</TableCell>
                  <TableCell className="text-sm">{s.work_type}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatTRY(s.total_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums text-brand font-medium">{formatTRY(s.hakedis_toplami || 0)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatTRY(s.kalan || s.total_amount)}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${STATUS_TONE[s.status]}`}>
                      {STATUS_LABEL[s.status]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(s.id)} className="h-8 w-8 p-0" title="PDF" data-testid={`sub-pdf-${s.id}`}>
                        <Printer className="w-3 h-3" />
                      </Button>
                      {tab !== "archive" && (
                        <Button size="sm" variant="ghost" onClick={() => setAttachmentSub(s)} className="h-8 w-8 p-0" title="Belgeler" data-testid={`sub-attach-${s.id}`}>
                          <Paperclip className="w-3 h-3" />
                        </Button>
                      )}
                      {tab !== "archive" && hasPerm("sozlesmeler.edit") && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="h-8 w-8 p-0" title="Düzenle" data-testid={`sub-edit-${s.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {tab !== "archive" && hasPerm("sozlesmeler.delete") && (
                        <Button size="sm" variant="ghost" onClick={() => archive(s.id)} className="h-8 w-8 p-0 text-destructive" title="Arşive Taşı" data-testid={`sub-archive-${s.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "archive" && hasPerm("sozlesmeler.edit") && (
                        <Button size="sm" variant="ghost" onClick={() => restore(s.id)} className="h-8 w-8 p-0" title="Geri Getir" data-testid={`sub-restore-${s.id}`}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "archive" && hasPerm("sozlesmeler.delete") && (
                        <Button size="sm" variant="ghost" onClick={() => purge(s.id)} className="h-8 w-8 p-0 text-destructive" title="Kalıcı Sil" data-testid={`sub-purge-${s.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground py-12">
                    {tab === "archive" ? "Arşivde sözleşme yok." : (
                      <div className="space-y-2">
                        <FileText className="w-8 h-8 mx-auto text-muted-foreground/40" />
                        <div>Henüz sözleşme yok. İlk taşeron/tedarikçi sözleşmenizi oluşturun.</div>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(EMPTY_FORM); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="sub-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? "Sözleşme Düzenle" : "Yeni Taşeron/Tedarikçi Sözleşmesi"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Taraflar */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Yüklenici (Taşeron/Tedarikçi/Personel)</Label>
                <Select value={form.cari_id} onValueChange={(v) => setForm({ ...form, cari_id: v })}>
                  <SelectTrigger data-testid="sub-cari"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    {cariler.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Proje</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                  <SelectTrigger data-testid="sub-project"><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>İş Tanımı</Label>
              <Input data-testid="sub-work-type" value={form.work_type} placeholder="Örn. Sıva işi, Boya, Kaba İnşaat, Elektrik tesisatı..."
                onChange={(e) => setForm({ ...form, work_type: e.target.value })} />
            </div>

            {/* Miktar & Fiyat */}
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label>Ölçü Türü</Label>
                <Select value={form.unit_type} onValueChange={(v) => setForm({ ...form, unit_type: v })}>
                  <SelectTrigger data-testid="sub-unit-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="m2">m²</SelectItem>
                    <SelectItem value="adet">adet</SelectItem>
                    <SelectItem value="goturu">Götürü (toplu)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Miktar {form.unit_type !== "goturu" && `(${UT_LABEL[form.unit_type]})`}</Label>
                <Input type="number" data-testid="sub-qty" value={form.quantity} disabled={form.unit_type === "goturu"}
                  onChange={(e) => recalcTotal(form.unit_price, e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Birim Fiyat</Label>
                <MoneyInput data-testid="sub-unit-price" value={form.unit_price} disabled={form.unit_type === "goturu"}
                  onChange={(n) => recalcTotal(n, form.quantity)} />
              </div>
              <div className="space-y-2">
                <Label>Toplam Bedel</Label>
                <MoneyInput data-testid="sub-total" value={form.total_amount}
                  onChange={(n) => setForm({ ...form, total_amount: n })} />
              </div>
            </div>

            {/* Tarihler */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Başlangıç Tarihi</Label>
                <Input type="date" data-testid="sub-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Teslim Tarihi</Label>
                <Input type="date" data-testid="sub-end" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="sub-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="taslak">Taslak</SelectItem>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="tamamlandi">Tamamlandı</SelectItem>
                    <SelectItem value="iptal">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Plan */}
            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Ödeme Takvimi</Label>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Plan toplam: <span className="font-medium text-foreground">{formatTRY(planTotal)}</span>
                    {form.total_amount > 0 && (
                      <span className={`ml-2 ${Math.abs(planTotal - form.total_amount) < 0.01 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                        {Math.abs(planTotal - form.total_amount) < 0.01 ? "✓ eşleşiyor" : `(fark ${formatTRY(form.total_amount - planTotal)})`}
                      </span>
                    )}
                  </span>
                  <Button type="button" size="sm" variant="outline" onClick={addPlanRow} data-testid="sub-add-plan-row">
                    <Plus className="w-3 h-3 mr-1" /> Ödeme Ekle
                  </Button>
                </div>
              </div>
              {(form.payment_plan || []).length === 0 ? (
                <div className="text-xs text-muted-foreground italic py-3 text-center border border-dashed rounded-md">
                  Henüz ödeme satırı yok. "Ödeme Ekle" ile başlayın.
                </div>
              ) : (
                <div className="border border-border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/40">
                        <TableHead className="w-40">Vade</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead className="text-right w-40">Tutar</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.payment_plan.map((p, i) => (
                        <TableRow key={i} data-testid={`plan-row-${i}`}>
                          <TableCell>
                            <Input type="date" value={p.date} onChange={(e) => updatePlanRow(i, "date", e.target.value)} className="h-8" />
                          </TableCell>
                          <TableCell>
                            <Input value={p.description || ""} placeholder="Örn. 1. Hakediş" onChange={(e) => updatePlanRow(i, "description", e.target.value)} className="h-8" />
                          </TableCell>
                          <TableCell className="text-right">
                            <MoneyInput value={p.amount} onChange={(n) => updatePlanRow(i, "amount", n)} className="h-8 text-right" />
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => removePlanRow(i)} className="h-8 w-8 p-0 text-destructive" data-testid={`plan-remove-${i}`}>
                              <X className="w-3 h-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notlar / Ek Şartlar</Label>
              <Textarea data-testid="sub-notes" value={form.notes} rows={3}
                placeholder="Malzeme, kalite, ceza koşulları, ek şartlar..."
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="sub-save" onClick={save} disabled={!form.cari_id || !form.project_id || !form.work_type || !form.total_amount}>
              {editingId ? "Kaydet" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attachments dialog — upload signed PDF, invoices, etc. */}
      <Dialog open={!!attachmentSub} onOpenChange={(o) => { if (!o) setAttachmentSub(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sub-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {attachmentSub?.contract_no} · Sözleşme Belgeleri
            </DialogTitle>
          </DialogHeader>
          {attachmentSub && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                İmzalı sözleşme taramasını, ek anlaşmaları veya kaşeli faturaları buraya yükleyin. PDF, JPG veya PNG desteklenir.
              </p>
              <UnitAttachments
                subcontract={attachmentSub}
                canEdit={hasPerm("sozlesmeler.edit")}
                defaultLabel="İmzalı Sözleşme"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
